/**
 * Utility to convert PlatinumData to ParsedTable format for chart rendering
 */

import type { PlatinumData } from '../../../types/carbon/models/PlatinumData'
import type { ParsedTable, DataType } from '../types'

/**
 * Converts PlatinumTableData to ParsedTable format
 *
 * @param platinumData - The PlatinumData object from the backend
 * @param dataType - Which data type to extract ('freq' | 'colPct' | 'rowPct')
 * @returns ParsedTable object or null if conversion fails
 */
export function convertPlatinumToTable(
  platinumData: PlatinumData,
  dataType: DataType = 'freq'
): ParsedTable | null {
  try {
    // Extract top axis (columns) and side axis (rows)
    const topAxis = platinumData.top
    const sideAxis = platinumData.side
    const cells = platinumData.cells

    if (!topAxis || !sideAxis || !cells) {
      console.error('Missing required data in Platinum format')
      return null
    }

    // Get column labels
    const columnVecs = topAxis.vecs || []
    const columnHeaders = columnVecs.map((vec) => vec.label || '')

    // Get row labels
    const rowVecs = sideAxis.vecs || []
    const rowLabels = rowVecs.map((vec) => {
      const label = vec.label || ''
      const vecType = vec.type || ''

      // If the vec type is Base and label is "Total", change to "Weighted Total"
      if (vecType.toLowerCase() === 'base' && label === 'Total') {
        return 'Weighted Total'
      }

      return label
    })

    // Process cells - extract values based on selected data type
    const rows = cells.rows || []
    const tableRows: string[][] = []

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex]
      const rowCells = row.cell || []
      const tableRow: string[] = [rowLabels[rowIndex] || '']

      for (let colIndex = 0; colIndex < columnHeaders.length; colIndex++) {
        const cell = rowCells[colIndex]
        if (cell) {
          let value: number | undefined

          switch (dataType) {
            case 'freq':
              value = cell.freq
              break
            case 'colPct':
              value = cell.colpc
              break
            case 'rowPct':
              value = cell.rowpc
              break
          }

          // Format based on data type
          if (value !== undefined && Number.isNaN(value)) {
            // #1181: a point blanked for a zero base (see blankZeroBasePoints).
            // '' parses to null in the generators (toNumber), so ECharts
            // leaves a gap instead of plotting a fake zero.
            tableRow.push('')
          } else if (value !== undefined) {
            if (dataType === 'freq') {
              // Frequencies plot to 1 decimal place (#1808), not to an
              // integer: a BIPs score of 3.4 must draw as 3.4. Whole counts
              // are unaffected ("12", not "12.0").
              tableRow.push((Math.round(value * 10) / 10).toString())
            } else {
              // For percentages, keep as decimal with more precision
              tableRow.push(value.toFixed(4))
            }
          } else {
            tableRow.push('0')
          }
        } else {
          tableRow.push('0')
        }
      }

      tableRows.push(tableRow)
    }

    // Create ParsedTable format
    const parsedTable: ParsedTable = {
      headers: [''].concat(columnHeaders), // First header is empty for row labels
      rows: tableRows,
      rowCnt: tableRows.length,
      colCnt: columnHeaders.length + 1, // +1 for row label column
    }

    return parsedTable
  } catch (error) {
    console.error('Error converting Platinum data to table format:', error)
    return null
  }
}

/**
 * Extract all row labels from PlatinumData
 *
 * @param platinumData - The PlatinumData object
 * @returns Array of row label strings
 */
export function extractRowLabels(platinumData: PlatinumData): string[] {
  const sideAxis = platinumData.side
  if (!sideAxis || !sideAxis.vecs) return []

  return sideAxis.vecs.map((vec) => {
    const label = vec.label || ''
    const vecType = vec.type || ''

    // If the vec type is Base and label is "Total", change to "Weighted Total"
    if (vecType.toLowerCase() === 'base' && label === 'Total') {
      return 'Weighted Total'
    }

    return label
  })
}

/**
 * Extract all column labels from PlatinumData
 *
 * @param platinumData - The PlatinumData object
 * @returns Array of column label strings
 */
export function extractColumnLabels(platinumData: PlatinumData): string[] {
  const topAxis = platinumData.top
  if (!topAxis || !topAxis.vecs) return []

  return topAxis.vecs.map((vec) => vec.label || '')
}

/**
 * Extract all row types from PlatinumData
 *
 * @param platinumData - The PlatinumData object
 * @returns Array of type strings for each row
 */
export function extractRowTypes(platinumData: PlatinumData): string[] {
  const sideAxis = platinumData.side
  if (!sideAxis || !sideAxis.vecs) return []

  return sideAxis.vecs.map((vec) => vec.type || '')
}

/**
 * Extract all column types from PlatinumData
 *
 * @param platinumData - The PlatinumData object
 * @returns Array of type strings for each column
 */
export function extractColumnTypes(platinumData: PlatinumData): string[] {
  const topAxis = platinumData.top
  if (!topAxis || !topAxis.vecs) return []

  return topAxis.vecs.map((vec) => vec.type || '')
}

/**
 * Filter out Base-type fields from a list of labels
 *
 * @param labels - Array of field labels
 * @param types - Array of field types corresponding to labels
 * @returns Filtered array containing only non-Base fields
 */
export function filterNonBaseFields(labels: string[], types: string[]): string[] {
  return labels.filter((_, index) => {
    const type = types[index] || ''
    return type.toLowerCase() !== 'base'
  })
}

/**
 * Transpose a ParsedTable (swap rows and columns)
 *
 * @param table - The ParsedTable to transpose
 * @returns Transposed ParsedTable
 */
export function transposeParsedTable(table: ParsedTable): ParsedTable {
  // Extract row labels (first element of each row)
  const rowLabels = table.rows.map(row => row[0])

  // Extract column headers (skip the first empty header)
  const columnHeaders = table.headers.slice(1)

  // Build transposed rows
  const transposedRows: string[][] = []

  // For each original column (now becomes a row)
  for (let colIdx = 0; colIdx < columnHeaders.length; colIdx++) {
    const newRow: string[] = [columnHeaders[colIdx]] // Row label is the old column header

    // For each original row (now becomes a column)
    for (let rowIdx = 0; rowIdx < table.rows.length; rowIdx++) {
      // Get the value at [rowIdx, colIdx+1] (+1 to skip row label)
      newRow.push(table.rows[rowIdx][colIdx + 1])
    }

    transposedRows.push(newRow)
  }

  // New headers: empty first element + old row labels
  const transposedHeaders = ['', ...rowLabels]

  return {
    headers: transposedHeaders,
    rows: transposedRows,
    rowCnt: transposedRows.length,
    colCnt: transposedHeaders.length,
  }
}
